/**
 * ResponsivePopover — one disclosure pattern for "extra detail on demand":
 * a Popover on desktop, a bottom-sheet Drawer on mobile.
 *
 * Extracted from NoteSummaryButton (the AI-summary popover), which is where
 * the pattern was proven. Anything that would otherwise inline-expand and
 * push the page around should use this instead — the content floats, so the
 * surrounding layout never shifts. That matters most inside the Bird thread,
 * where an expanding sources list moves the message you're reading.
 *
 * Composition constraints, both learned the hard way:
 *  - `PopoverTrigger` must wrap the trigger element DIRECTLY. Wrapping a
 *    Tooltip swallows the trigger props + anchor ref, because Tooltip
 *    forwards its ref to the floating tooltip content. So the Tooltip goes
 *    on the OUTSIDE, around a plain Box.
 *  - The mobile branch has no PopoverTrigger to inject an onClick, so the
 *    trigger element is cloned with one.
 *
 * Body content is rendered only while open (`isLazy` + the `isOpen &&` guard),
 * so a heavy panel costs nothing until someone asks for it.
 *
 * Focus handling comes from Chakra: the popover traps focus and returns it to
 * the trigger on close, and Esc closes. Don't reimplement either.
 */

import {
  Box,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverCloseButton,
  PopoverContent,
  PopoverHeader,
  PopoverTrigger,
  Portal,
  useBreakpointValue,
  useDisclosure,
} from '@chakra-ui/react';
import { cloneElement, type MouseEventHandler, type ReactElement, type ReactNode } from 'react';

import { Tooltip } from './Tooltip';

interface ResponsivePopoverProps {
  /**
   * The control that opens the panel. Must forward props + ref (a Chakra
   * Button / IconButton does). Rendered as-is on desktop; cloned with an
   * onClick on mobile.
   */
  readonly trigger: ReactElement<{ onClick?: MouseEventHandler }>;
  /** Tooltip on the trigger. Omit when the trigger already carries a label. */
  readonly tooltip?: string;
  /** Header row — small-caps label style, matching the AI-summary panel. */
  readonly title: ReactNode;
  /** Panel body. Only mounted while open. */
  readonly children: ReactNode;
  readonly width?: string;
  readonly placement?: 'top-start' | 'top-end' | 'bottom-start' | 'bottom-end';
  /** Extra element rendered next to the trigger (e.g. a count badge). */
  readonly triggerAdornment?: ReactNode;
  /** Fired when the panel opens — for the analytics event on disclosure. */
  readonly onOpened?: () => void;
}

const HEADER_SX = {
  fontSize: 'xs',
  textTransform: 'uppercase',
  letterSpacing: 'wide',
  color: 'text.muted',
  display: 'flex',
  alignItems: 'center',
  gap: 1.5,
} as const;

export function ResponsivePopover({
  trigger,
  tooltip,
  title,
  children,
  width = '340px',
  placement = 'bottom-start',
  triggerAdornment,
  onOpened,
}: ResponsivePopoverProps) {
  const { isOpen, onOpen: openPanel, onClose } = useDisclosure();
  const onOpen = () => {
    openPanel();
    onOpened?.();
  };
  const isMobile = useBreakpointValue({ base: true, md: false }) || false;

  const withTooltip = (content: ReactElement) =>
    tooltip ? (
      <Tooltip label={tooltip} placement="top">
        {content}
      </Tooltip>
    ) : (
      content
    );

  if (isMobile) {
    // Chakra's PopoverTrigger injects aria-haspopup/expanded/controls and
    // COMPOSES onClick. There's no equivalent on the drawer path, so do both
    // by hand — otherwise the mobile trigger announces as a plain button and
    // silently drops any onClick the caller put on it.
    const mobileTriggerProps: Record<string, unknown> = {
      onClick: (event: React.MouseEvent<HTMLElement>) => {
        trigger.props.onClick?.(event);
        if (!event.defaultPrevented) (isOpen ? onClose : onOpen)();
      },
      'aria-haspopup': 'dialog',
      'aria-expanded': isOpen,
      // No aria-controls: Chakra's useModal spreads the caller's props and
      // THEN overwrites `id` with its own, so any id we set on DrawerContent
      // is clobbered and the reference dangles. haspopup + expanded already
      // convey the pattern; a dangling reference is worse than none.
    };
    return (
      <>
        {withTooltip(
          <Box position="relative" display="inline-flex">
            {cloneElement(trigger, mobileTriggerProps)}
            {triggerAdornment}
          </Box>,
        )}
        <Drawer isOpen={isOpen} onClose={onClose} placement="bottom">
          <DrawerOverlay />
          {/* The house bottom-sheet shape — same as DestinationPicker and
              MetaPanelHost: capped at 85dvh, grab
              handle, safe-area padding.
              `maxH` is the load-bearing part, not decoration. Chakra's bottom
              drawer sizes to its content and caps at `100vh`, and on mobile
              Chrome `100vh` is the URL-bar-less height — so a panel with a
              dozen rows grew until its TOP sat above the visible viewport,
              taking the header and the close button with it. dvh tracks the
              actual visible viewport, and 85 of it leaves the overlay visible
              so the sheet still reads as a sheet. */}
          <DrawerContent
            borderTopRadius="2xl"
            bg="bg.elevated"
            maxH="85dvh"
            pb="env(safe-area-inset-bottom)"
          >
            <Box pt={2} pb={1} display="flex" justifyContent="center" flexShrink={0}>
              <Box w={9} h={1} bg="border.muted" borderRadius="full" />
            </Box>
            <DrawerHeader {...HEADER_SX} flexShrink={0}>
              {title}
            </DrawerHeader>
            <DrawerCloseButton top={4} />
            {/* No `overflowY` here on purpose: Chakra's drawer `baseStyleBody`
                is already `{ flex: 1, overflow: 'auto' }`, so the body always
                scrolled. What it lacked was a BOUND to scroll within — that is
                what `maxH` above supplies. */}
            <DrawerBody pb={6}>{isOpen && children}</DrawerBody>
          </DrawerContent>
        </Drawer>
      </>
    );
  }

  return (
    <Popover isOpen={isOpen} onOpen={onOpen} onClose={onClose} placement={placement} isLazy>
      {withTooltip(
        <Box position="relative" display="inline-flex">
          <PopoverTrigger>{trigger}</PopoverTrigger>
          {triggerAdornment}
        </Box>,
      )}
      {/* Portalled: the Bower thread is an `overflow-y: auto` container, so an
          in-place popover gets clipped at the panel edge and scrolls away with
          the message it belongs to. Every consumer of this primitive wants the
          panel to float above whatever contains the trigger. */}
      <Portal>
        {/* `bg.elevated` (white), not `bg.surface`. Since the 2026-09 flip
            `bg.surface` IS the parchment these float over — the Sources popover
            sits on the composer, which is the same #F8F7F4, so the panel had no
            edge but its hairline. Chakra's own Menu and Modal moved to
            `bg.elevated` for this; these did not. */}
        <PopoverContent
          w={width}
          maxW="90vw"
          bg="bg.elevated"
          borderColor="border.muted"
          boxShadow="lg"
          // The WHOLE panel is bounded by the viewport, not just its body, and
          // that is what stops it being sliced off at the top edge.
          //
          // Popper's preventOverflow can shift a panel back into view, but it
          // cannot shrink one: an element TALLER than the viewport overflows at
          // one end whatever placement it is given, and with `top-start` (the
          // chat composer's Sources button) that end is the top — so the header
          // and the first row went off-screen with no scrollbar to bring them
          // back. Capping the container makes every arrangement fit, and the
          // body below scrolls within whatever is left.
          maxH="calc(100vh - 32px)"
          display="flex"
          flexDirection="column"
        >
          <PopoverArrow bg="bg.elevated" />
          <PopoverHeader {...HEADER_SX} flexShrink={0}>
            {title}
          </PopoverHeader>
          <PopoverCloseButton />
          {/* Capped and scrollable, because the caller can't know how many
              rows it has: a Bower answer grounded in 15 sources rendered a
              panel taller than the viewport, so the last sources — and the
              close button's neighbours — were simply unreachable. Viewport-
              relative so it still behaves on a short window; the header and
              close button stay pinned outside the scroll area.
              `minH={0}` is what lets it shrink below its content inside the
              flex column above — without it a flex item floors at its content
              height and the container's cap does nothing. */}
          <PopoverBody py={3} flex="1 1 auto" minH={0} maxH="min(60vh, 420px)" overflowY="auto">
            {isOpen && children}
          </PopoverBody>
        </PopoverContent>
      </Portal>
    </Popover>
  );
}
